'use client';

import { forwardRef, HTMLAttributes, ReactNode, useState, Children, type CSSProperties } from 'react';
import styles from './Avatar.module.css';
import { getDatavizContrastText } from '../utils/contrastText';
import Icon from '../primitives/Icon';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';
type AvatarShape = 'circle' | 'square';
type AvatarRing = 'danger' | 'success' | 'brand';

const SIZE_PX: Record<AvatarSize, number> = { xs: 24, sm: 32, md: 40, lg: 56 };

const RING_CLASS: Record<AvatarRing, string | undefined> = {
  danger: styles.ringDanger,
  success: styles.ringSuccess,
  brand: styles.ringBrand,
};

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  name?: string;
  icon?: ReactNode;
  /** Preset size, or an exact pixel number for a one-off dimension. */
  size?: AvatarSize | number;
  /** 'circle' (default) or 'square' — a rounded square whose corner radius scales with size. */
  shape?: AvatarShape;
  /** Shows a green presence dot on the top-right edge (on the ring for circles, the corner for squares). */
  online?: boolean;
  /** Draws a 2px status ring around the avatar, e.g. 'danger' for an expired subscription. */
  ring?: AvatarRing;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  return (parts[0]?.[0] ?? '').toUpperCase();
}

/* 20 dataviz hues, each with tone + contrast text computed from foundation rules */
const AVATAR_PALETTE = Array.from({ length: 20 }, (_, i) => {
  const hueIdx = i + 1;
  const toneIdx = 1; // tone 1 = 90% lightness (light bg)
  return {
    bg: `var(--dataviz-${hueIdx}-${toneIdx})`,
    text: getDatavizContrastText(hueIdx, toneIdx),
  };
});

function nameToColorPair(name: string): { bg: string; text: string } {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  // RANGE-PROVED (D9 class 3): `% AVATAR_PALETTE.length` over a fixed-length (20) module
  // constant is always in [0, 19] — provable from the array literal alone, no interaction needed.
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]!;
}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(({
  src,
  alt = '',
  name = '',
  icon,
  size = 'md',
  shape = 'circle',
  online = false,
  ring,
  className = '',
  ...props
}, ref) => {
  const [imgError, setImgError] = useState(false);
  const showImage = src && !imgError;
  const showInitials = !showImage && name;
  const showIcon = !showImage && !showInitials;

  const numeric = typeof size === 'number';
  const px = numeric ? (size as number) : SIZE_PX[size as AvatarSize];

  const colorStyle: CSSProperties | undefined = showInitials
    ? { backgroundColor: nameToColorPair(name).bg, color: nameToColorPair(name).text }
    // Image fills the frame — drop the fallback bg so it doesn't halo through the
    // rounded-corner clip as a light ring around the photo.
    : showImage
      ? { backgroundColor: 'transparent' }
      : undefined;

  // Numeric sizes are one-offs — dimension them inline (presets use CSS classes).
  // Square corner radius scales with size (~30%): 28px→8px, 40px→12px.
  const sizeStyle: CSSProperties | undefined = numeric
    ? {
        width: px,
        height: px,
        fontSize: Math.round(px * 0.4),
        ...(shape === 'square' ? { borderRadius: Math.round(px * 0.3) } : {}),
      }
    : undefined;

  const avatarStyle = colorStyle || sizeStyle ? { ...colorStyle, ...sizeStyle } : undefined;

  // Presence dot: inline size/position for numeric avatars (presets are handled in CSS).
  let dotStyle: CSSProperties | undefined;
  if (numeric && online) {
    const dotPx = Math.max(5, Math.round(px * 0.22));
    const dr = (dotPx + 3) / 2; // + 1.5px ring on each side
    const r = px / 2;
    const offset = shape === 'square'
      ? -dr                                      // centre on the box corner
      : r - dr - (-0.5 + r + dr) / Math.SQRT2;   // on the 45° ring so it just touches
    dotStyle = { width: dotPx, height: dotPx, top: offset, right: offset };
  }

  return (
    <div
      ref={ref}
      className={[styles.avatar, numeric ? '' : styles[size as AvatarSize], shape === 'square' ? styles.square : '', ring ? RING_CLASS[ring] : '', className].filter(Boolean).join(' ')}
      style={avatarStyle}
      role="img"
      aria-label={alt || name || 'Avatar'}
      {...props}
    >
      {showImage && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={alt || name}
          className={styles.image}
          onError={() => setImgError(true)}
        />
      )}
      {showInitials && <span className={styles.initials}>{getInitials(name)}</span>}
      {showIcon && (
        <span className={styles.iconFallback}>
          {icon || <Icon name="person" size="md" />}
        </span>
      )}
      {online && <span className={styles.presenceDot} style={dotStyle} aria-label="Online" />}
    </div>
  );
});

Avatar.displayName = 'Avatar';

/* ===== AVATAR GROUP ===== */
export interface AvatarGroupProps extends HTMLAttributes<HTMLDivElement> {
  max?: number;
  size?: AvatarSize;
  children: ReactNode;
}

const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(({
  max = 5,
  size = 'md',
  children,
  className = '',
  ...props
}, ref) => {
  const childArray = Children.toArray(children);
  const visible = childArray.slice(0, max);
  const overflow = childArray.length - max;

  return (
    <div
      ref={ref}
      className={[styles.group, className].filter(Boolean).join(' ')}
      {...props}
    >
      {visible}
      {overflow > 0 && (
        <div className={[styles.avatar, styles[size], styles.overflow].filter(Boolean).join(' ')}>
          <span className={styles.overflowText}>+{overflow}</span>
        </div>
      )}
    </div>
  );
});

AvatarGroup.displayName = 'AvatarGroup';

export default Avatar;
export { AvatarGroup };
